import React, { useState, useEffect } from 'react';
import { getEduProApplications } from '../services/api';
import { EduProContract } from './EduProContract';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import {
  Loader2, ShieldCheck, CheckCircle, AlertTriangle, ChevronDown, ChevronUp,
  FileSignature, User, Users, Sparkles, ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const STATUS = {
  draft: { label: 'Borrador', cls: 'bg-slate-100 text-slate-700' },
  approved: { label: 'Aprobado', cls: 'bg-emerald-100 text-emerald-700' },
  manual_review: { label: 'En revisión', cls: 'bg-amber-100 text-amber-700' },
  active: { label: 'Activo · Contrato firmado', cls: 'bg-[#0A2463] text-white' },
};

const CHECK_LABELS = {
  document_ocr: 'Documento (OCR)', liveness: 'Prueba de vida', face_match: 'Biometría facial',
  registry: 'Registro oficial', tax: 'Validación fiscal', aml: 'Listas AML/PEP', credit_bureau: 'Buró de crédito',
};

const SubjectStatus = ({ title, icon: Icon, data }) => {
  if (!data) return null;
  const ok = data.identity_ok;
  return (
    <div className="border border-slate-200 rounded-xl p-4">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Icon className="w-4 h-4 text-[#0A2463]" />
          <span className="font-semibold text-sm text-slate-800">{title}</span>
        </div>
        <Badge className={ok ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}>
          {ok ? 'Verificado' : 'Revisión'}
        </Badge>
      </div>
      <div className="grid grid-cols-1 gap-1.5">
        {(data.checks || []).map((c) => (
          <div key={c.id} className="flex items-center justify-between text-xs">
            <span className="text-slate-500">{CHECK_LABELS[c.id] || c.id}</span>
            <span className={`flex items-center gap-1 font-medium ${c.status === 'passed' ? 'text-emerald-600' : 'text-amber-600'}`}>
              {c.status === 'passed' ? <CheckCircle className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
              {c.status === 'passed' ? 'OK' : 'Revisar'}
            </span>
          </div>
        ))}
      </div>
      {data.credit && (
        <p className="text-[11px] text-slate-400 mt-3 pt-2 border-t border-slate-100">
          Score: <strong className="text-slate-600">{data.credit.score}</strong> · Perfil: <strong className="text-slate-600 capitalize">{data.credit.tier}</strong>
        </p>
      )}
    </div>
  );
};

const EduProAppCard = ({ app, onOpenContract }) => {
  const [expanded, setExpanded] = useState(false);
  const st = STATUS[app.status] || { label: app.status, cls: 'bg-slate-100 text-slate-700' };
  const money = (v) =>
    new Intl.NumberFormat(app.currency === 'COP' ? 'es-CO' : 'es-MX', {
      style: 'currency', currency: app.currency || 'MXN', minimumFractionDigits: 0,
    }).format(v || 0);
  const v = app.verification || {};
  const flag = app.country === 'CO' ? '🇨🇴' : '🇲🇽';
  const signed = app.status === 'active';

  return (
    <div className="border border-slate-200 rounded-2xl p-4 sm:p-5" data-testid={`edupro-app-${app.application_id}`}>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="bg-[#0A2463] rounded-xl w-11 h-11 flex items-center justify-center flex-shrink-0">
            <span className="text-lg">{flag}</span>
          </div>
          <div>
            <p className="font-bold text-slate-900 text-sm">EduPro · {app.applicant?.full_name || 'Solicitud'}</p>
            <p className="text-xs text-slate-500">{app.term_months} meses · Aval: {app.guarantor?.full_name || '—'}</p>
          </div>
        </div>
        <Badge className={st.cls}>{st.label}</Badge>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-4 text-center bg-slate-50 rounded-xl p-3">
        <div>
          <p className="text-[10px] text-slate-400 uppercase">Monto</p>
          <p className="font-bold text-sm text-[#0A2463]">{money(app.amount_approved || app.principal)}</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-400 uppercase">Pago/mes</p>
          <p className="font-bold text-sm text-[#E91E8C]">{app.monthly_payment ? money(app.monthly_payment) : '—'}</p>
        </div>
        <div>
          <p className="text-[10px] text-slate-400 uppercase">Tasa</p>
          <p className="font-bold text-sm text-slate-700">{app.interest_rate ? `${(app.interest_rate * 100).toFixed(1)}%` : '—'}</p>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mt-4">
        {(app.status === 'approved' || signed) && (
          <Button size="sm" onClick={() => onOpenContract(app.application_id)} className="bg-[#E91E8C] hover:bg-[#c21473] text-white" data-testid={`edupro-contract-btn-${app.application_id}`}>
            <FileSignature className="w-4 h-4 mr-1.5" /> {signed ? 'Ver contrato' : 'Firmar contrato'}
          </Button>
        )}
        {v.applicant && (
          <Button size="sm" variant="outline" onClick={() => setExpanded((e) => !e)} data-testid={`edupro-verif-toggle-${app.application_id}`}>
            <ShieldCheck className="w-4 h-4 mr-1.5" /> Estado de verificación
            {expanded ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
          </Button>
        )}
      </div>

      {expanded && v.applicant && (
        <div className="grid sm:grid-cols-2 gap-3 mt-4">
          <SubjectStatus title="Solicitante" icon={User} data={v.applicant} />
          <SubjectStatus title="Aval / Co-deudor" icon={Users} data={v.guarantor} />
        </div>
      )}
    </div>
  );
};

export const EduProDashboardSection = () => {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [contractId, setContractId] = useState(null);

  const load = () => {
    setLoading(true);
    getEduProApplications()
      .then((r) => setApps(r.data || []))
      .catch(() => setApps([]))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  if (loading) return <div className="py-12 text-center"><Loader2 className="w-7 h-7 animate-spin text-[#0A2463] mx-auto" /></div>;

  if (apps.length === 0) {
    return (
      <div className="text-center py-10" data-testid="edupro-empty">
        <div className="inline-flex items-center gap-2 bg-[#E91E8C]/10 rounded-full px-3 py-1 mb-4">
          <Sparkles className="w-4 h-4 text-[#E91E8C]" />
          <span className="text-xs font-semibold text-[#E91E8C]">Financiamiento EduPro · 1,800 USD</span>
        </div>
        <h3 className="text-lg font-semibold text-slate-900">Aún no tienes solicitudes EduPro</h3>
        <p className="text-sm text-slate-500 mb-5">Estudia en España y financia tu programa con un aval.</p>
        <Link to="/edupro/apply">
          <Button className="bg-[#0A2463] hover:bg-[#08204f] text-white" data-testid="edupro-start-btn">
            Iniciar solicitud EduPro <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4" data-testid="edupro-apps-list">
      {apps.map((app) => (
        <EduProAppCard key={app.application_id} app={app} onOpenContract={setContractId} />
      ))}
      {contractId && (
        <EduProContract applicationId={contractId} onClose={() => { setContractId(null); load(); }} onSigned={() => load()} />
      )}
    </div>
  );
};

export default EduProDashboardSection;
